import { useCallback, useEffect, useRef, useState } from 'react';
import { api, fdate, fmt, onBusy, STATUS_FR, toggleTheme } from './api.js';
import { selectFeaturedMatch } from '../backend/featured.js';
import { parseUtcDate } from '../backend/time.mjs';
import StreamPlayer from './StreamPlayer.jsx';

function Loader({ label = 'Chargement…' }) {
  return <div className="loader" role="status"><span className="spinner" />{label}</div>;
}

function TopBar() {
  const [busy, setBusy] = useState(false);
  useEffect(() => onBusy((n) => setBusy(n > 0)), []);
  return <div className={`topbar${busy ? ' on' : ''}`} />;
}

function KinshasaClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="kinshasa-clock" aria-label="Date et heure">
      <span>{now.toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Africa/Kinshasa',
      })}</span>
      <time dateTime={now.toISOString()}>{now.toLocaleTimeString('fr-FR', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Africa/Kinshasa',
      })}</time>
    </div>
  );
}

function useToast() {
  const [t, setT] = useState(null);
  const timer = useRef();
  const toast = useCallback((msg, err = false) => {
    setT({ msg, err });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setT(null), 3500);
  }, []);
  return [t, toast];
}

function usePoll(fn, ms, deps) {
  useEffect(() => {
    let alive = true;
    const run = () => alive && fn();
    run();
    const id = setInterval(run, ms);
    return () => { alive = false; clearInterval(id); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

export default function App() {
  const [user, setUser] = useState(undefined);
  const [cfg, setCfg] = useState(null);
  const [toastState, toast] = useToast();

  const lastBalance = useRef(null);
  const refresh = useCallback(async () => {
    const d = await api('/me');
    const prev = lastBalance.current;
    lastBalance.current = d.user ? d.user.balance : null;
    if (d.user && prev !== null && d.user.balance > prev) {
      toast(`💰 +${fmt(d.user.balance - prev)} · nouveau solde ${fmt(d.user.balance)}`);
    }
    setUser(d.user);
    setCfg(d.config);
  }, [toast]);
  useEffect(() => { refresh().catch((e) => toast(e.message, true)); }, [refresh, toast]);
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => refresh().catch(() => {}), 15_000);
    return () => clearInterval(id);
  }, [user, refresh]);

  const guard = useCallback((fn) => async (...a) => {
    try { await fn(...a); } catch (e) { toast(e.message, true); }
  }, [toast]);

  return (
    <>
      <TopBar />
      {user === undefined ? <Loader /> : user
        ? <Shell user={user} cfg={cfg} refresh={refresh} toast={toast} guard={guard} onLogout={() => setUser(null)} />
        : <Auth onDone={guard(refresh)} guard={guard} />}
      <div id="toast" className={toastState ? `show${toastState.err ? ' err' : ''}` : ''}>{toastState?.msg}</div>
    </>
  );
}

// ---------- Auth ----------
function Auth({ onDone, guard }) {
  const [reg, setReg] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [f, setF] = useState({ email: '', password: '', confirm: '', adminCode: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = guard(async (e) => {
    e.preventDefault();
    if (reg && f.password !== f.confirm) throw new Error('Les mots de passe ne correspondent pas');
    const body = { email: f.email, password: f.password };
    if (reg && admin && f.adminCode) body.adminCode = f.adminCode;
    await api(reg ? '/auth/register' : '/auth/login', body);
    await onDone();
  });

  return (
    <div className="wrap auth">
      <div className="logo"><span className="logo-ico">🏆</span>BERFOOT</div>
      <p className="muted">Pariez entre joueurs sur les matchs de foot. Mise en CDF, paiement par mobile money.</p>
      <div className="card">
        <h2>{reg ? 'Inscription' : 'Connexion'}</h2>
        <form onSubmit={submit}>
          <label>Email<input type="email" required autoComplete="email" value={f.email} onChange={set('email')} /></label>
          <label>Mot de passe
            <input type="password" required minLength={8} autoComplete={reg ? 'new-password' : 'current-password'} value={f.password} onChange={set('password')} />
          </label>
          {reg && (
            <>
              <label>Confirmer le mot de passe
                <input type="password" required minLength={8} autoComplete="new-password" value={f.confirm} onChange={set('confirm')} />
              </label>
              {admin
                ? <label>Code administrateur<input type="password" autoComplete="off" autoFocus value={f.adminCode} onChange={set('adminCode')} /></label>
                : <button type="button" className="link" onClick={() => setAdmin(true)}>Je suis administrateur</button>}
            </>
          )}
          <button className="btn primary" type="submit">{reg ? 'Créer mon compte' : 'Se connecter'}</button>
        </form>
        <p className="muted">
          {reg ? 'Déjà inscrit ? ' : 'Pas encore de compte ? '}
          <button className="link" onClick={() => setReg(!reg)}>{reg ? 'Se connecter' : "S'inscrire"}</button>
        </p>
      </div>
      <button className="btn" onClick={toggleTheme}>◐ Thème</button>
    </div>
  );
}

// ---------- Shell ----------
const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);
const ICONS = {
  matches: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3',
  openBets: 'M4 5h16v14H4zM8 9h8M8 13h3M15 13h1M8 17h8M7 5V3m10 2V3',
  bets: 'M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4zM13 6v12',
  wallet: 'M4 7h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zM4 7l11-3v3M16 14h2',
  streams: 'M3 7h18v13H3zM7 3l5 4 5-4M10 11l5 2.5-5 2.5z',
  admin: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  sun: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  out: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
};

function ActivityTicker() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  usePoll(async () => { try { setItems((await api('/activity')).items); } catch { /* silencieux */ } }, 20_000, []);
  useEffect(() => {
    if (items.length < 2) return undefined;
    const id = setInterval(() => setI((n) => n + 1), 4000);
    return () => clearInterval(id);
  }, [items.length]);
  if (!items.length) return null;
  const it = items[i % items.length];
  return (
    <div className="ticker" role="status">
      <div className="wrap">
        <div key={it.id} className="ticker-item">
          <span className={`dot ${it.state}`} />
          <span className="ticker-txt">
            {it.state === 'waiting' ? 'Pari en attente' : 'Pari accepté'} sur <b>{it.match}</b> · {fmt(it.stake)}
            {it.state === 'waiting' ? ' · en attente d’un adversaire…' : ' · en cours'}
          </span>
        </div>
      </div>
    </div>
  );
}

function Notifier({ userId, toast, refresh }) {
  const key = `bf_notif_${userId}`;
  const [perm, setPerm] = useState(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);

  usePoll(async () => {
    try {
      const stored = localStorage.getItem(key);
      const d = await api(`/notifications?after=${stored ?? 0}`);
      setUnread(d.unread);
      if (stored === null) { localStorage.setItem(key, String(d.last)); return; }
      if (!d.items.length) return;
      refresh().catch(() => {});
      const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : null;
      for (const n of d.items) {
        toast(`${n.title} · ${n.body}`);
        if (perm === 'granted' && document.hidden) {
          const opts = { body: n.body, icon: new URL('icons/icon-192.png', import.meta.url).href, tag: `bf-${n.id}` };
          try {
            if (reg) await reg.showNotification(n.title, opts); else new Notification(n.title, opts);
          } catch (err) {
            console.error('Impossible d’afficher la notification', err);
          }
        }
        localStorage.setItem(key, String(n.id));
      }
    } catch (err) { console.error('Impossible de traiter les notifications', err); }
  }, 10_000, [perm]);

  const loadHistory = async (targetPage = page) => {
    setLoading(true);
    try {
      const data = await api(`/notifications?page=${targetPage}`);
      setItems(data.items);
      setPage(data.page);
      setPages(data.pages);
      setUnread(data.unread);
    } catch (err) {
      console.error('Impossible de charger le centre de notifications', err);
      toast('Impossible de charger les notifications', true);
    } finally {
      setLoading(false);
    }
  };
  const toggleCenter = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) loadHistory(1);
  };
  const markRead = async (id) => {
    try {
      const data = await api('/notifications/read', { id });
      setUnread(data.unread);
      await loadHistory(page);
    } catch (err) {
      console.error('Impossible de marquer la notification comme lue', err);
      toast('Impossible de mettre à jour la notification', true);
    }
  };
  const markAllRead = async () => {
    try {
      const data = await api('/notifications/read', { all: true });
      setUnread(data.unread);
      await loadHistory(page);
    } catch (err) {
      console.error('Impossible de marquer les notifications comme lues', err);
      toast('Impossible de mettre à jour les notifications', true);
    }
  };

  return (
    <div className="notification-anchor">
      <button className="round notification-bell" title="Centre de notifications" aria-label={`Centre de notifications, ${unread} non lue(s)`} aria-expanded={open} onClick={toggleCenter}>
        🔔{unread > 0 && <span className="notification-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <section className="notification-center" aria-label="Centre de notifications">
          <div className="row between">
            <h3>Notifications</h3>
            <button className="link" onClick={() => setOpen(false)}>Fermer</button>
          </div>
          {unread > 0 && <button className="link" onClick={markAllRead}>Tout marquer comme lu</button>}
          {perm === 'default' && (
            <button className="link notification-permission" onClick={async () => {
              try { setPerm(await Notification.requestPermission()); }
              catch (err) { console.error('Impossible d’activer les notifications', err); toast('Impossible d’activer les notifications', true); }
            }}>Activer les alertes sur cet appareil</button>
          )}
          {loading ? <p className="muted">Chargement…</p> : items.length === 0
            ? <p className="muted">Aucune notification pour le moment.</p>
            : (
              <div className="notification-list">
                {items.map((n) => (
                  <article className={`notification-item${n.read ? '' : ' unread'}`} key={n.id}>
                    <div className="notification-copy">
                      <strong>{n.title}</strong>
                      <p>{n.body}</p>
                      <time className="meta">{fdate(`${n.created_at.replace(' ', 'T')}Z`)}</time>
                    </div>
                    {!n.read && <button className="link" onClick={() => markRead(n.id)}>Marquer comme lue</button>}
                  </article>
                ))}
              </div>
            )}
          {pages > 1 && (
            <div className="row notification-pages">
              <button className="btn" disabled={page <= 1 || loading} onClick={() => loadHistory(page - 1)}>←</button>
              <span className="meta">Page {page} / {pages}</span>
              <button className="btn" disabled={page >= pages || loading} onClick={() => loadHistory(page + 1)}>→</button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

const tvDaysRemaining = (until) =>
  until ? Math.ceil((new Date(`${until.replace(' ', 'T')}Z`).getTime() - Date.now()) / 86_400_000) : null;

function StreamAccess({ user, cfg, refresh, guard, go }) {
  const active = user.role === 'admin' || (tvDaysRemaining(user.tvSubscriptionUntil) > 0);
  if (active) return <StreamPlayer />;

  const price = cfg.tvSubscriptionPrice;
  const subscribe = guard(async () => {
    if (!window.confirm(`L’abonnement TV coûte ${fmt(price)} aujourd’hui, puis sera renouvelé chaque mois au même prix si votre solde est suffisant. Confirmez-vous ?`)) return;
    await api('/tv/subscribe', {});
    await refresh();
  });

  return (
    <section className="card tv-paywall">
      <span className="stream-eyebrow">BERFOOT TV</span>
      <h1>Abonnement nécessaire</h1>
      <p>Pour regarder les chaînes en direct, activez votre abonnement TV à <strong>{fmt(price)} par mois</strong>.</p>
      <p className="muted">Le montant sera débité de votre porte-monnaie. Le renouvellement est mensuel et dépend du solde disponible.</p>
      {user.balance < price ? (
        <>
          <p>Solde actuel : <strong>{fmt(user.balance)}</strong>. Il vous manque <strong>{fmt(price - user.balance)}</strong>.</p>
          <button className="btn primary" onClick={() => go({ name: 'wallet' })}>Recharger mon porte-monnaie</button>
        </>
      ) : (
        <button className="btn primary" onClick={subscribe}>Activer mon abonnement · {fmt(price)}</button>
      )}
    </section>
  );
}

function Shell({ user, cfg, refresh, toast, guard, onLogout }) {
  const [view, setView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const id = Number(params.get('match'));
    if (!Number.isSafeInteger(id) || id < 1) return { name: 'matches' };
    const offerId = Number(params.get('offer'));
    return {
      name: 'match',
      id,
      ...(Number.isSafeInteger(offerId) && offerId > 0 ? { offerId } : {}),
    };
  });
  const tabs = [
    ['matches', 'Matchs'],
    ['openBets', 'Paris ouverts'],
    ['bets', 'Mes paris'],
    ['wallet', 'Porte-monnaie'],
    ['streams', 'Flux TV'],
  ];
  if (user.role === 'admin') tabs.push(['admin', 'Admin']);
  const active = view.name === 'match' ? 'matches' : view.name;
  const ctx = { cfg, refresh, toast, guard, go: setView };

  return (
    <>
      <header>
        <div className="wrap bar">
          <button className="logo" title="Retour aux matchs" onClick={() => setView({ name: 'matches' })}><span className="logo-ico">🏆</span>BERFOOT</button>
          <nav className="topnav">
            {tabs.map(([k, l]) => (
              <button key={k} className={active === k ? 'active' : ''} onClick={() => setView({ name: k })}>
                <Icon d={ICONS[k]} /><span>{l}</span>
              </button>
            ))}
          </nav>
          <div className="bar-right">
            <Notifier userId={user.id} toast={toast} refresh={refresh} />
            <button className="pill" onClick={() => setView({ name: 'wallet' })}>
              <Icon d={ICONS.wallet} />{fmt(user.balance)}
            </button>
            <button className="round" title="Changer de thème" onClick={toggleTheme}><Icon d={ICONS.sun} /></button>
            <button className="round" title="Déconnexion" onClick={guard(async () => { await api('/auth/logout', {}); onLogout(); })}><Icon d={ICONS.out} /></button>
          </div>
        </div>
      </header>
      <KinshasaClock />
      <ActivityTicker />
      {user.role !== 'admin' && (() => {
        const days = tvDaysRemaining(user.tvSubscriptionUntil);
        return days !== null && days >= 0 && days <= 15
          ? <div className="wrap"><p className="tv-reminder" role="status">
            Votre abonnement TV prend fin dans {days} jour{days === 1 ? '' : 's'}.
          </p></div>
          : null;
      })()}
      <main className="wrap">
        {view.name === 'matches' && <Matches {...ctx} />}
        {view.name === 'match' && <MatchDetail id={view.id} initialSide={view.side} sharedOfferId={view.offerId} {...ctx} />}
        {view.name === 'openBets' && <OpenBets {...ctx} />}
        {view.name === 'bets' && <Bets {...ctx} />}
        {view.name === 'wallet' && <Wallet {...ctx} />}
        {view.name === 'streams' && <StreamAccess user={user} cfg={cfg} refresh={refresh} guard={guard} go={setView} />}
        {view.name === 'admin' && <Admin {...ctx} />}
      </main>
      <nav className="bottom">
        {tabs.map(([k, l]) => (
          <button key={k} className={active === k ? 'active' : ''} onClick={() => setView({ name: k })}>
            <Icon d={ICONS[k]} /><span>{l}</span>
          </button>
        ))}
      </nav>
    </>
  );
}

function StatusBadge({ m }) {
  return m.status === 'LIVE'
    ? <span className="badge live">EN DIRECT {m.minute ?? ''}</span>
    : <span className="badge">{STATUS_FR[m.status] ?? m.status}</span>;
}

const fshort = (iso) =>
  iso ? parseUtcDate(iso).toLocaleString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Kinshasa' }).replace(' à', ',') : '';

async function shareOffer(match, offer, toast) {
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('match', String(match.id));
  url.searchParams.set('offer', String(offer.id));
  const shareData = {
    title: 'Pari BerFoot',
    text: `${offer.by} propose un pari sur ${match.home} vs ${match.away} : mise ${fmt(offer.stake)}.`,
    url: url.toString(),
  };
  if (navigator.share) {
    try {
      await navigator.share(shareData);
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    }
    return;
  }
  if (!navigator.clipboard?.writeText) throw new Error('Le partage de lien n’est pas disponible sur cet appareil.');
  await navigator.clipboard.writeText(shareData.url);
  toast('Lien du pari copié');
}

function Crest({ src, name, big }) {
  const [bad, setBad] = useState(false);
  useEffect(() => setBad(false), [src]);
  return (
    <span className={`crest${big ? ' big' : ''}`}>
      {src && !bad ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBad(true)} /> : name.slice(0, 1)}
    </span>
  );
}

// ---------- Matchs ----------
function Matches({ go, guard, cfg }) {
  const [tab, setTab] = useState('upcoming');
  const [q, setQ] = useState('');
  const [league, setLeague] = useState('');
  const [matches, setMatches] = useState(null);
  const load = guard(async () => setMatches((await api(`/matches?tab=${tab}&q=${encodeURIComponent(q)}`)).matches));
  usePoll(load, 30_000, [tab, q]);

  const leagues = [];
  for (const m of matches ?? []) {
    const l = leagues.find((x) => x.name === m.league);
    if (l) l.n += 1; else leagues.push({ name: m.league ?? 'Autres', n: 1 });
  }
  leagues.sort((a, b) => b.n - a.n);
  const shown = (matches ?? []).filter((m) => !league || (m.league ?? 'Autres') === league);
  const featured = tab !== 'finished' && shown.length
    ? selectFeaturedMatch(shown)
    : null;
  const rest = shown.filter((m) => m !== featured);

  return (
    <>
      <div className="chips">
        {[['live', 'En cours'], ['upcoming', 'À venir'], ['finished', 'Terminés']].map(([k, l]) => (
          <button key={k} className={`chip${tab === k ? ' on' : ''}`} onClick={() => { setTab(k); setLeague(''); setMatches(null); }}>{l}</button>
        ))}
        <input className="search" type="search" placeholder="Équipe ou championnat…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="chips scroll">
        <button className={`chip${!league ? ' on' : ''}`} onClick={() => setLeague('')}>Toutes</button>
        {leagues.map((l) => (
          <button key={l.name} className={`chip${league === l.name ? ' on' : ''}`} onClick={() => setLeague(l.name)}>
            {l.name} <b>{l.n}</b>
          </button>
        ))}
      </div>
      {!matches && <Loader label="Chargement des matchs…" />}
      {matches && shown.length === 0 && <p className="muted">Aucun match.</p>}
      {featured && (
        <div className="hero" onClick={() => go({ name: 'match', id: featured.id })}>
          <div className="hero-top"><span className="flame">🔥 À LA UNE</span><span className="muted">{fshort(featured.kickoff)}</span></div>
          <div className="hero-teams">
            <div className="team"><Crest big src={featured.homeLogo} name={featured.home} /><div className="tname up">{featured.home}</div></div>
            <div className="vs big">VS</div>
            <div className="team"><Crest big src={featured.awayLogo} name={featured.away} /><div className="tname up">{featured.away}</div></div>
          </div>
          <div className="hero-foot">{(featured.league ?? '').toUpperCase()} · PRENDS POSITION →</div>
        </div>
      )}
      <div className="grid">
        {rest.map((m) => <MatchCard key={m.id} m={m} cfg={cfg} go={go} />)}
      </div>
    </>
  );
}

function MatchCard({ m, cfg, go }) {
  const open = m.status === 'Scheduled' || m.status === 'LIVE';
  const pick = (side) => (e) => { e.stopPropagation(); go({ name: 'match', id: m.id, side }); };
  return (
    <div className="mcard" onClick={() => go({ name: 'match', id: m.id })}>
      <div className="mcard-top">
        <span className="league">{m.league}</span>
        <span>{m.status === 'LIVE' ? <StatusBadge m={m} /> : fshort(m.kickoff)}</span>
      </div>
      <div className="mcard-teams">
        <div className="team"><Crest src={m.homeLogo} name={m.home} /><div className="tname">{m.home}</div></div>
        <div className="vs">{m.homeScore != null ? <span className="score">{m.homeScore} - {m.awayScore}</span> : 'VS'}</div>
        <div className="team"><Crest src={m.awayLogo} name={m.away} /><div className="tname">{m.away}</div></div>
      </div>
      {open
        ? (
          <div className="odds">
            <button onClick={pick('home')}><span>{m.home}</span><b>x{cfg.odds}</b></button>
            <button onClick={pick('away')}><span>{m.away}</span><b>x{cfg.odds}</b></button>
          </div>
        )
        : <div className="odds"><StatusBadge m={m} /></div>}
      {m.open_offers > 0 && <div className="offers">{m.open_offers} pari(s) ouvert(s) à accepter</div>}
    </div>
  );
}

function OpenBets({ refresh, toast, guard, go }) {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState(null);
  const load = guard(async () => {
    const params = new URLSearchParams({ tab, q: search });
    setItems((await api(`/bets/open?${params}`)).items);
  });
  usePoll(load, 20_000, [tab, search]);

  const accept = (offer) => guard(async () => {
    if (!confirm(`Miser ${fmt(offer.stake)} CDF sur ${offer.takeSide === 'home' ? offer.home : offer.away} ? La mise sera bloquée jusqu'à la fin du match.`)) return;
    await api(`/bets/${offer.id}/accept`, {});
    toast('Pari accepté ! Mise bloquée jusqu’à la fin du match');
    await refresh();
    await load();
  });
  const share = (offer) => guard(async () => {
    await shareOffer(
      { id: offer.matchId, home: offer.home, away: offer.away },
      { ...offer, by: offer.by },
      toast,
    );
  });

  return (
    <section>
      <div className="row between">
        <div>
          <h2>Paris ouverts</h2>
          <p className="meta">Découvrez les paris proposés par les joueurs et prenez l’équipe adverse.</p>
        </div>
        <button className="btn" onClick={() => go({ name: 'matches' })}>Créer un pari</button>
      </div>
      <div className="chips">
        <button className={`chip${tab === 'all' ? ' on' : ''}`} onClick={() => { setItems(null); setTab('all'); }}>Tous les matchs</button>
        <button className={`chip${tab === 'live' ? ' on' : ''}`} onClick={() => { setItems(null); setTab('live'); }}>En direct</button>
        <input
          className="search"
          type="search"
          placeholder="Rechercher une équipe ou un joueur…"
          value={search}
          onChange={(event) => { setItems(null); setSearch(event.target.value); }}
        />
      </div>
      {!items && <Loader label="Chargement des paris ouverts…" />}
      {items && items.length === 0 && (
        <p className="muted">{search ? 'Aucun pari ne correspond à votre recherche.' : 'Aucun pari ouvert pour le moment.'}</p>
      )}
      <div className="offer-list">
        {items?.map((offer) => {
          const chosen = offer.creatorSide === 'home' ? offer.home : offer.away;
          const opposing = offer.takeSide === 'home' ? offer.home : offer.away;
          const match = { id: offer.matchId, home: offer.home, away: offer.away };
          return (
            <article className="offer open-bet" key={offer.id}>
              <div className="open-bet-info">
                <div className="row between">
                  <strong>{offer.home} vs {offer.away}</strong>
                  {offer.matchStatus === 'LIVE'
                    ? <span className="badge live">EN DIRECT {offer.minute ?? ''}</span>
                    : <span className="meta">{fshort(offer.kickoff)}</span>}
                </div>
                <div className="meta">{offer.league}</div>
                {offer.matchStatus === 'LIVE' && offer.homeScore !== null && (
                  <div className="score">{offer.homeScore} - {offer.awayScore}</div>
                )}
                <div className="teams">{offer.by} <span className="muted">a misé</span> {fmt(offer.stake)} CDF <span className="muted">sur</span> {chosen}</div>
                <div className="meta">Vous choisissez {opposing} · gain net potentiel {fmt(offer.winNet)} CDF</div>
              </div>
              <div className="row">
                {offer.mine
                  ? <button className="btn" disabled>Votre pari</button>
                  : <button className="btn primary" onClick={accept(offer)}>Accepter</button>}
                <button className="btn" onClick={share(offer)}>Partager</button>
                <button className="btn" onClick={() => go({ name: 'match', id: offer.matchId, offerId: offer.id })}>Voir</button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function MatchDetail({ id, initialSide, sharedOfferId, go, cfg, refresh, toast, guard }) {
  const [data, setData] = useState(null);
  const [side, setSide] = useState(initialSide ?? 'home');
  const [stake, setStake] = useState(cfg.minStake);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const load = guard(async () => setData(await api(`/matches/${id}`)));
  usePoll(load, 20_000, [id]);
  if (!data) return <Loader label="Chargement du match…" />;
  const { match: m, offers: allOffers } = data;
  const term = search.trim().toLowerCase();
  const offers = allOffers.filter((o) =>
    (filter === 'all' || o.creatorSide === filter) && (!term || o.by.toLowerCase().includes(term)));
  const count = (s) => allOffers.filter((o) => o.creatorSide === s).length;
  const open = m.status === 'Scheduled' || m.status === 'LIVE';
  const name = (s) => (s === 'home' ? m.home : m.away);
  const after = (msg) => async () => { toast(msg); await refresh(); await load(); };
  const share = (o) => guard(() => shareOffer(
    { id: m.id, home: m.home, away: m.away },
    { ...o, by: o.by },
    toast,
  ));

  const create = guard(async (e) => {
    e.preventDefault();
    await api('/bets', { matchId: m.id, side, stake: Number(stake) });
    await after('Pari créé : mise bloquée en attendant un adversaire')();
  });
  const accept = (o) => guard(async () => {
    if (!confirm(`Miser ${fmt(o.stake)} sur ${name(o.takeSide)} ? Impossible d'annuler une fois accepté.`)) return;
    await api(`/bets/${o.id}/accept`, {});
    await after("Pari accepté ! Mise bloquée jusqu'à la fin du match")();
  });
  const cancel = (o) => guard(async () => {
    await api(`/bets/${o.id}/cancel`, {});
    await after('Pari retiré, mise remboursée')();
  });

  return (
    <>
      <button className="btn" onClick={() => go({ name: 'matches' })}>← Matchs</button>
      <div className="hero static">
        <div className="hero-top"><span className="muted">{m.league}</span><span className="muted">{fshort(m.kickoff)}</span></div>
        <div className="hero-teams">
          <div className="team"><Crest big src={m.homeLogo} name={m.home} /><div className="tname up">{m.home}</div></div>
          <div className="vs big">{m.homeScore != null ? `${m.homeScore} - ${m.awayScore}` : 'VS'}</div>
          <div className="team"><Crest big src={m.awayLogo} name={m.away} /><div className="tname up">{m.away}</div></div>
        </div>
        <div className="hero-foot"><StatusBadge m={m} /></div>
      </div>
      <div className="split">
      {open ? (
        <form className="card" onSubmit={create}>
          <h3>Lancer un pari</h3>
          <div className="seg">
            {['home', 'away'].map((s) => (
              <button key={s} type="button" className={`btn${side === s ? ' sel' : ''}`} onClick={() => setSide(s)}>{name(s)}</button>
            ))}
          </div>
          <label>Mise (CDF, {cfg.minStake} – {cfg.maxStake})
            <input type="number" min={cfg.minStake} max={cfg.maxStake} step={1} required value={stake} onChange={(e) => setStake(e.target.value)} />
          </label>
          <p className="meta">
            Votre mise est bloquée tout de suite. Si un joueur prend l'équipe adverse, le pari est verrouillé jusqu'à la fin.
            Nul ou match annulé : chacun récupère sa mise. Commission {cfg.commissionPercent}% sur le gain.
          </p>
          <button className="btn primary" type="submit">Miser</button>
        </form>
      ) : <p className="muted">Les paris sont fermés pour ce match.</p>}
      <div className="card offers-panel">
        <h3>Joueurs qui ont parié ({allOffers.length})</h3>
        <p className="meta">Choisissez un joueur : vous prenez l'équipe adverse. Une fois accepté, le pari ne peut plus être retiré.</p>
        <div className="chips">
          <button className={`chip${filter === 'all' ? ' on' : ''}`} onClick={() => setFilter('all')}>Tous <b>{allOffers.length}</b></button>
          {['home', 'away'].map((s) => (
            <button key={s} className={`chip${filter === s ? ' on' : ''}`} onClick={() => setFilter(s)}>
              Ont choisi {name(s)} <b>{count(s)}</b>
            </button>
          ))}
        </div>
        <input className="search" type="search" placeholder="Rechercher un joueur…" value={search} onChange={(e) => setSearch(e.target.value)} />
        {sharedOfferId && !allOffers.some((o) => String(o.id) === String(sharedOfferId)) && (
          <p className="meta">Ce pari n’est plus disponible. Il a peut-être déjà été accepté ou retiré.</p>
        )}
        {offers.length === 0 && (
          <p className="muted">{allOffers.length === 0
            ? "Aucun pari ouvert. Lancez le vôtre : un autre joueur pourra prendre l'équipe adverse."
            : 'Aucun joueur ne correspond.'}</p>
        )}
        <div className="offer-list">
          {offers.map((o) => (
            <div key={o.id} className={`offer${String(o.id) === String(sharedOfferId) ? ' shared-offer' : ''}`}>
              <div>
                <div className="teams">{o.by} <span className="muted">a choisi</span> {name(o.creatorSide)}</div>
                <div className="meta">Mise {fmt(o.stake)} · vous prenez {name(o.takeSide)} · gain net {fmt(o.winNet)}</div>
              </div>
              <div className="row">
                {o.mine
                  ? <button className="btn danger" onClick={cancel(o)}>Retirer</button>
                  : <button className="btn primary" disabled={!open} onClick={accept(o)}>Accepter</button>}
                <button className="btn" onClick={share(o)}>Partager</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </>
  );
}

// ---------- Mes paris ----------
function Bets({ refresh, guard }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const load = guard(async (requestedPage = page) => {
    const result = await api(`/bets/mine?page=${requestedPage}`);
    setData(result);
    setPage(result.page);
  });
  usePoll(load, 30_000, [page]);
  const label = (b) => ({
    open: "En attente d'adversaire", matched: 'Verrouillé', refunded: 'Remboursé', cancelled: 'Retiré',
    settled: b.result === 'won' ? 'Gagné' : 'Perdu',
  }[b.status]);

  return (
    <>
      <h2>Mes paris</h2>
      {!data && <Loader label="Chargement de vos paris…" />}
      {data?.total === 0 && <p className="muted">Aucun pari pour le moment.</p>}
      {data?.bets.map((b) => (
        <div key={b.id} className="card row between">
          <div>
            <div className="teams">{b.home} vs {b.away}</div>
            <div className="meta">Vous : {b.side === 'home' ? b.home : b.away} · mise {fmt(b.stake)} · {fdate(b.kickoff)}</div>
          </div>
          <div>
            <span className={`badge ${b.result === 'won' ? 'ok' : b.result === 'lost' ? 'bad' : ''}`}>{label(b)}</span>
            {b.payout > 0 && <div className="pos">+{fmt(b.payout)}</div>}
            {b.status === 'open' && (
              <button className="btn danger" onClick={guard(async () => { await api(`/bets/${b.id}/cancel`, {}); await refresh(); await load(page); })}>Retirer</button>
            )}
          </div>
        </div>
      ))}
      {data?.pages > 1 && (
        <div className="row" style={{ alignItems: 'center', justifyContent: 'center', marginTop: 10 }}>
          <button className="btn" disabled={page <= 1} onClick={() => load(page - 1)}>← Précédent</button>
          <span className="meta">Page {page} / {data.pages} · {data.total} paris</span>
          <button className="btn" disabled={page >= data.pages} onClick={() => load(page + 1)}>Suivant →</button>
        </div>
      )}
    </>
  );
}

// ---------- Portefeuille ----------
const LEDGER_FR = {
  deposit: 'Recharge', withdrawal: 'Retrait', withdrawal_refund: 'Retrait échoué (remboursé)',
  bet_stake: 'Mise bloquée', bet_refund: 'Mise remboursée', bet_win: 'Gain',
};
const PAYMENT_STATUS_FR = { pending: 'En attente', success: 'Réussi', failed: 'Échoué' };

function PayForm({ kind, cfg, onSubmit }) {
  const [f, setF] = useState({ amount: '', network: Object.keys(cfg.networks)[0], phone: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const dep = kind === 'deposit';
  const requestedAmount = Number(f.amount);
  const chargeAmount = dep && Number.isSafeInteger(requestedAmount) && requestedAmount >= cfg.minDeposit
    ? Math.ceil(
      (requestedAmount + cfg.depositNetBonus) * 10_000
      / (10_000 - cfg.depositFeeBasisPoints),
    )
    : null;
  return (
    <form className="card pay-form" onSubmit={(e) => { e.preventDefault(); onSubmit({ amount: Number(f.amount), network: f.network, phone: f.phone }); }}>
      <h3>{dep ? 'Recharger' : 'Retirer'}</h3>
      <label>{dep ? 'Montant à créditer (CDF)' : 'Montant (CDF)'}<input type="number" step={1} required min={dep ? cfg.minDeposit : cfg.minWithdrawal} value={f.amount} onChange={set('amount')} /></label>
      {chargeAmount !== null && (
        <p className="muted">
          Débit estimé sur votre mobile money : <strong>{fmt(chargeAmount)} CDF</strong>
          {' '} (frais estimés à 3,2 %; votre solde BerFoot sera crédité de {fmt(requestedAmount)} CDF).
        </p>
      )}
      <label>Réseau
        <select value={f.network} onChange={set('network')}>
          {Object.entries(cfg.networks).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      <label>Numéro mobile money<input type="tel" required placeholder="0812345678" autoComplete="tel" value={f.phone} onChange={set('phone')} /></label>
      <button className="btn primary pay-submit" type="submit">{dep ? 'Payer' : 'Retirer maintenant'}</button>
    </form>
  );
}

function PaymentModal({ modal, onClose }) {
  const dep = modal.kind === 'deposit';
  const { state } = modal;
  const view = {
    pending: [<span className="spinner" />, dep ? 'Paiement en cours…' : 'Retrait en cours…',
      dep ? 'Validez la demande sur votre téléphone (code PIN mobile money). Ne fermez pas cette page.' : 'Envoi de votre argent vers votre mobile money.'],
    success: ['✅', dep ? 'Recharge reçue !' : 'Retrait envoyé !', dep ? 'Votre solde a été crédité.' : 'Vous allez recevoir l\'argent sur votre mobile money.'],
    failed: ['❌', 'Paiement échoué', 'Le paiement a été refusé ou annulé. Rien n\'a été débité.'],
    timeout: ['⏳', 'Toujours en attente', dep
      ? 'Le solde sera crédité automatiquement dès que le paiement sera confirmé.'
      : 'Le retrait sera confirmé automatiquement dès que PawaPay aura répondu.'],
  }[state];
  return (
    <div className="overlay" role="dialog" aria-modal="true">
      <div className="modal">
        <div className="modal-ico">{view[0]}</div>
        <h2>{view[1]}</h2>
        <div className={`big ${state === 'success' ? 'pos' : ''}`}>{dep && state === 'success' ? '+' : ''}{fmt(modal.amount)}</div>
        <p className="muted">{view[2]}</p>
        {state === 'pending' && dep && modal.chargeAmount && (
          <p className="muted">Débit estimé sur votre mobile money : {fmt(modal.chargeAmount)} CDF.</p>
        )}
        {state === 'pending' && modal.url && (
          <a className="btn primary" href={modal.url} target="_blank" rel="noopener noreferrer">Ouvrir la page de paiement</a>
        )}
        {state !== 'pending' && <button className="btn primary" onClick={onClose}>OK</button>}
      </div>
    </div>
  );
}

function Wallet({ cfg, refresh, toast, guard }) {
  const [w, setW] = useState(null);
  const [paymentPage, setPaymentPage] = useState(1);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const load = guard(async (page = paymentPage) => {
    const data = await api(`/wallet?page=${page}`);
    setW(data);
    setPaymentPage(data.paymentPage);
  });
  usePoll(load, 30_000, [paymentPage]);

  const [modal, setModal] = useState(null);

  const follow = async (p, amount) => {
    if (p.checkoutUrl && /^https:\/\//.test(p.checkoutUrl)) window.open(p.checkoutUrl, '_blank', 'noopener');
    for (let i = 0; i < 45 && alive.current; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      const s = await api(`/wallet/payments/${p.id}`);
      if (s.status === 'success') {
        setModal({ state: 'success', kind: 'deposit', amount });
        await refresh(); await load(w?.paymentPage ?? 1);
        return;
      }
      if (s.status === 'failed') { setModal({ state: 'failed', kind: 'deposit', amount }); return; }
    }
    setModal({ state: 'timeout', kind: 'deposit', amount });
  };
  const deposit = async (b) => {
    setModal({ state: 'pending', kind: 'deposit', amount: b.amount, url: null });
    try {
      const p = await api('/wallet/deposit', b);
      if (p.checkoutUrl) setModal({ state: 'pending', kind: 'deposit', amount: b.amount, chargeAmount: p.chargeAmount, url: p.checkoutUrl });
      else setModal({ state: 'pending', kind: 'deposit', amount: b.amount, chargeAmount: p.chargeAmount, url: null });
      await follow(p, b.amount);
    } catch (e) {
      setModal(null);
      toast(e.message, true);
    }
  };
  const followWithdrawal = async (payment, amount) => {
    for (let i = 0; i < 45 && alive.current; i++) {
      await new Promise((resolve) => setTimeout(resolve, 4000));
      const status = await api(`/wallet/payments/${payment.id}`);
      if (status.status === 'success') {
        setModal({ state: 'success', kind: 'withdraw', amount });
        await refresh();
        await load(w?.paymentPage ?? 1);
        return;
      }
      if (status.status === 'failed') {
        setModal({ state: 'failed', kind: 'withdraw', amount });
        await refresh();
        await load(w?.paymentPage ?? 1);
        return;
      }
    }
    if (alive.current) setModal({ state: 'timeout', kind: 'withdraw', amount });
  };
  const withdraw = async (b) => {
    setModal({ state: 'pending', kind: 'withdraw', amount: b.amount });
    try {
      const payment = await api('/wallet/withdraw', b);
      await followWithdrawal(payment, b.amount);
    } catch (e) {
      setModal(null);
      toast(e.message, true);
    }
  };
  if (!w) return <Loader label="Chargement du porte-monnaie…" />;

  return (
    <>
      {modal && <PaymentModal modal={modal} onClose={() => setModal(null)} />}
      <div className="card"><div className="meta">Solde disponible</div><div className="big">{fmt(w.balance)}</div></div>
      <div className="pay-grid">
        <PayForm kind="deposit" cfg={cfg} onSubmit={deposit} />
        <PayForm kind="withdraw" cfg={cfg} onSubmit={withdraw} />
      </div>
      <h3>Mouvements du solde</h3>
      <div className="card">
        <Paged
          rows={w.ledger}
          head={['Date', 'Opération', 'Montant']}
          row={(l, i) => (
            <tr key={i}>
              <td>{fdate(`${l.created_at.replace(' ', 'T')}Z`)}</td>
              <td>{LEDGER_FR[l.type] ?? l.type}</td>
              <td className={l.amount >= 0 ? 'pos' : 'neg'}>{l.amount >= 0 ? '+' : ''}{fmt(l.amount)}</td>
            </tr>
          )}
          size={10}
        />
      </div>
      <h3>Historique des paiements</h3>
      <div className="card">
        {w.payments.length === 0
          ? 'Aucun paiement'
          : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead><tr><th>Date</th><th>Opération</th><th>Réseau</th><th>Montant</th><th>Statut</th></tr></thead>
                  <tbody>{w.payments.map((p) => (
                    <tr key={p.id}>
                      <td>{fdate(`${p.created_at.replace(' ', 'T')}Z`)}</td>
                      <td>{p.kind === 'deposit' ? 'Recharge' : 'Retrait'}</td>
                      <td>{cfg.networks[p.network] ?? p.network}</td>
                      <td>{fmt(p.amount)}</td>
                      <td>{PAYMENT_STATUS_FR[p.status] ?? p.status}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              {w.paymentPages > 1 && (
                <div className="row" style={{ alignItems: 'center', justifyContent: 'center', marginTop: 10 }}>
                  <button className="btn" disabled={w.paymentPage === 1} onClick={() => load(w.paymentPage - 1)}>← Précédent</button>
                  <span className="meta">Page {w.paymentPage} / {w.paymentPages} · {w.paymentCount} paiements</span>
                  <button className="btn" disabled={w.paymentPage >= w.paymentPages} onClick={() => load(w.paymentPage + 1)}>Suivant →</button>
                </div>
              )}
            </>
          )}
      </div>
      {w.payments.some((p) => p.status === 'pending') && (
        <p className="muted">Des paiements sont en cours de confirmation ; ils sont vérifiés automatiquement.</p>
      )}
    </>
  );
}

// ---------- Admin ----------
function Paged({ rows, head, row, size = 10 }) {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const cur = Math.min(page, pages - 1);
  if (rows.length === 0) return 'Aucun';
  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{rows.slice(cur * size, (cur + 1) * size).map(row)}</tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="row" style={{ alignItems: 'center', justifyContent: 'center', marginTop: 10 }}>
          <button className="btn" disabled={cur === 0} onClick={() => setPage(cur - 1)}>← Précédent</button>
          <span className="meta">Page {cur + 1} / {pages} · {rows.length} lignes</span>
          <button className="btn" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)}>Suivant →</button>
        </div>
      )}
    </>
  );
}

function Admin({ toast, guard }) {
  const [s, setS] = useState(null);
  const [tvUsers, setTvUsers] = useState(null);
  const [tvUserPage, setTvUserPage] = useState(1);
  const [voidId, setVoidId] = useState('');
  const load = guard(async (page = tvUserPage) => {
    const [stats, subscriptions] = await Promise.all([
      api('/admin/stats'),
      api(`/admin/tv-users?page=${page}`),
    ]);
    setS(stats);
    setTvUsers(subscriptions);
    setTvUserPage(subscriptions.page);
  });
  usePoll(load, 30_000, [tvUserPage]);
  if (!s || !tvUsers) return <Loader label="Chargement de l'administration…" />;
  const Stat = ({ l, v }) => <div className="card"><div className="meta">{l}</div><div className="big">{v}</div></div>;

  return (
    <>
      <h2>Administration</h2>
      <div className="row">
        <Stat l="Utilisateurs" v={s.users} /><Stat l="Commissions gagnées" v={fmt(s.commission)} />
        <Stat l="Gain attendu (paris en cours)" v={fmt(s.pendingCommission)} />
        <Stat l="Soldes joueurs" v={fmt(s.walletTotal)} /><Stat l="Mises bloquées" v={fmt(s.lockedStakes)} />
      </div>
      <div className="row">
        <Stat l="Paris ouverts" v={s.openBets} /><Stat l="Paris verrouillés" v={s.matchedBets} /><Stat l="Matchs en base" v={s.matches} />
      </div>
      <h3>Abonnements BerFoot TV · {tvUsers.total} utilisateur(s)</h3>
      <div className="card">
        {tvUsers.users.length === 0
          ? <p className="muted">Aucun utilisateur inscrit.</p>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead><tr><th>Utilisateur</th><th>Abonnement TV</th><th>Solde CDF</th></tr></thead>
                <tbody>{tvUsers.users.map((tvUser) => {
                  const days = tvDaysRemaining(tvUser.tvSubscriptionUntil);
                  const name = tvUser.email.split('@')[0];
                  return (
                    <tr key={tvUser.email}>
                      <td><strong>{name.charAt(0).toLocaleUpperCase() + name.slice(1)}</strong><br /><span className="meta">{tvUser.email}</span></td>
                      <td>{days === null ? 'Aucun abonnement' : days > 0
                        ? <>Actif · reste {days} jour{days === 1 ? '' : 's'}<br /><span className="meta">Fin : {fdate(`${tvUser.tvSubscriptionUntil.replace(' ', 'T')}Z`)}</span></>
                        : 'Expiré'}</td>
                      <td>{fmt(tvUser.balance)}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        {tvUsers.pages > 1 && (
          <div className="row" style={{ alignItems: 'center', justifyContent: 'center', marginTop: 10 }}>
            <button className="btn" disabled={tvUsers.page <= 1} onClick={() => load(tvUsers.page - 1)}>← Précédent</button>
            <span className="meta">Page {tvUsers.page} / {tvUsers.pages}</span>
            <button className="btn" disabled={tvUsers.page >= tvUsers.pages} onClick={() => load(tvUsers.page + 1)}>Suivant →</button>
          </div>
        )}
      </div>
      <button className="btn" onClick={guard(async () => { const r = await api('/admin/sync', {}); toast(`${r.fixtures} matchs synchronisés`); await load(); })}>
        Synchroniser maintenant
      </button>
      <form className="card" onSubmit={guard(async (e) => {
        e.preventDefault();
        if (!confirm(`Annuler le match ${voidId} et rembourser tous les paris ?`)) return;
        const r = await api(`/admin/matches/${Number(voidId)}/void`, {});
        toast(`${r.refunded} pari(s) remboursé(s)`); await load();
      })}>
        <h3>Annuler un match (remboursement)</h3>
        <label>ID du match<input type="number" required value={voidId} onChange={(e) => setVoidId(e.target.value)} /></label>
        <button className="btn danger" type="submit">Annuler et rembourser</button>
      </form>
      <h3>Historique des paris</h3>
      <div className="card">
        <Paged
          rows={s.betHistory}
          head={['#', 'Match', 'Email → équipe', 'Adversaire → équipe', 'Mise', 'Statut', 'Gagnant']}
          row={(b) => (
            <tr key={b.id}>
              <td>{b.id}</td><td>{b.match}</td>
              <td>{b.creator} → <b>{b.creatorTeam}</b></td>
              <td>{b.taker ? <>{b.taker} → <b>{b.takerTeam}</b></> : '—'}</td>
              <td>{fmt(b.stake)}</td><td>{b.status}</td>
              <td>{b.winner ? `${b.winner} (+${fmt(b.payout)})` : '—'}</td>
            </tr>
          )}
        />
      </div>
      <h3>Historique des retraits</h3>
      <div className="card">
        <Paged
          rows={s.withdrawalHistory}
          head={['Date', 'Email', 'Montant', 'Réseau', 'Téléphone', 'Statut']}
          row={(p) => (
            <tr key={p.id}><td>{p.created_at}</td><td>{p.email}</td><td>{fmt(p.amount)}</td><td>{p.network}</td><td>{p.phone}</td><td>{p.status}</td></tr>
          )}
        />
      </div>
      <h3>Paiements en attente</h3>
      <div className="card">
        <Paged
          rows={s.pendingPayments}
          head={['Type', 'Montant', 'Réseau', 'Date']}
          row={(p) => (
            <tr key={p.id}><td>{p.kind}</td><td>{fmt(p.amount)}</td><td>{p.network}</td><td>{p.created_at}</td></tr>
          )}
        />
      </div>
    </>
  );
}
